/**
 * expenseFetch — fetch() wrapper that attaches a platform-api-audience Entra token
 * (scope-parameterized acquireToken(getExpenseScopes())) to every accounting-api
 * call and surfaces server errors as thrown ApiError. Cloned from the CRM api-fetch.
 *
 * SECURITY: the per-entity isolation boundary is SERVER-SIDE (per-entity DB identity
 * + RLS in the accounting-api). This client just calls the API — it must NOT and
 * does NOT re-implement that check.
 */
import { acquireToken } from '@/auth/msal';
import { expenseUrl, getExpenseScopes } from '@/expense/expense-config';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function expenseFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = await acquireToken(getExpenseScopes());
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // JSON bodies get the JSON content-type; multipart FormData must NOT — the browser sets
  // `multipart/form-data; boundary=…` itself, and overriding it breaks the upload.
  const isFormData =
    typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const resp = await fetch(expenseUrl(path), { ...init, headers });

  if (!resp.ok) {
    const text = await resp.text();
    let body: unknown = text;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      /* raw text */
    }
    const message =
      typeof body === 'object' &&
      body !== null &&
      'detail' in body &&
      typeof (body as { detail: unknown }).detail === 'string'
        ? (body as { detail: string }).detail
        : `${resp.status} ${resp.statusText}`;
    throw new ApiError(message, resp.status, body);
  }

  if (resp.status === 204) return undefined as T;
  if (resp.headers.get('content-type')?.includes('text/csv')) {
    return (await resp.text()) as T;
  }
  return (await resp.json()) as T;
}
